import React, {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {EyeIcon, TrashIcon} from "@heroicons/react/24/outline";
import axiosInstance from "../../utils/axiosInstance";
import {formatStatus, getStatusBadgeClass, truncateText} from "../../utils/Utils";
import DownloadModFileButton from "../../components/DownloadModFileButton";
import Notification from "../../components/Notification";
import { usePagedList } from "../../hooks/usePagedList";
import {
    Alert,
    Badge,
    ConfirmModal,
    Page,
    PageHeader,
    PageLoader,
    Pagination,
    Panel,
    SearchInput,
    TableShell,
    Td,
    TdEmpty,
    Th,
    Tr,
} from "../../components/ui";

const AllFiles = () => {
    const [files, setFiles] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [fileToDelete, setFileToDelete] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingToDelete, setLoadingToDelete] = useState(false);
    const [error, setError] = useState(null);
    const [showNotification, setShowNotification] = useState(false);

    useEffect(() => {
        const fetchFiles = async () => {
            try {
                const response = await axiosInstance.get("/files");
                setFiles(response.data);
            } finally {
                setLoading(false);
            }
        };

        fetchFiles();
    }, []);

    const handleDeleteClick = (file) => {
        setFileToDelete(file);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            setLoadingToDelete(true);
            await axiosInstance.delete(`/files/${fileToDelete.id}`);
            setFiles(files.filter((file) => file.id !== fileToDelete.id));
            setShowDeleteModal(false);
            setFileToDelete(null);

            setShowNotification(true);

            setTimeout(() => {
                setShowNotification(false);
            }, 3000);
            setLoadingToDelete(false);
        } catch (err) {
            setError("Failed to delete file. Please try again.");
        }
    };

    // Close the modal without deleting
    const handleCloseModal = () => {
        setShowDeleteModal(false);
        setFileToDelete(null);
    };

    // Filtered files based on search term
    const filteredFiles = files.filter(
        (file) =>
            file.file_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            file.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
            String(file.file_id).includes(searchTerm.toLowerCase())
    );

    const paged = usePagedList(filteredFiles, 25, searchTerm);

    if (loading) {
        return (
            <Page>
                <PageHeader title="All Files" onBack={() => window.history.back()}/>
                <PageLoader label="Loading files…"/>
            </Page>
        );
    }

    return (
        <Page>
            <PageHeader
                title="All Files"
                subtitle={`${files.length} file${files.length === 1 ? "" : "s"} in total`}
                onBack={() => window.history.back()}
            >
                <Badge tone="accent">{filteredFiles.length} shown</Badge>
            </PageHeader>

            {error && <Alert tone="error">{error}</Alert>}

            <SearchInput
                placeholder="Search by file id, file name, username…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />

            <Panel padded={false}>
                <TableShell className="rounded-[22px] border-0">
                    <thead>
                    <tr>
                        <Th>ID</Th>
                        <Th>Username</Th>
                        <Th>File Name</Th>
                        <Th>Credit</Th>
                        <Th>Price Status</Th>
                        <Th>Status</Th>
                        <Th>Feasible</Th>
                        <Th align="center">Mod File</Th>
                        <Th align="center">Actions</Th>
                    </tr>
                    </thead>
                    <tbody>
                    {filteredFiles.length === 0 ? (
                        <TdEmpty colSpan={9}>No files match your search.</TdEmpty>
                    ) : null}
                    {paged.visible.map((file) => (
                        <Tr key={file.id}>
                            <Td className="font-mono text-xs">{file.file_id}</Td>
                            <Td className="font-medium text-gray-800 dark:text-slate-200">
                                {truncateText(file.username, 15)}
                            </Td>
                            <Td title={file.file_name}>{truncateText(file.file_name, 15)}</Td>
                            <Td className="font-semibold">{file.price}</Td>
                            <Td>
                                <span className={getStatusBadgeClass(file.price_status)}>
                                    {formatStatus(file.price_status)}
                                </span>
                            </Td>
                            <Td>
                                <span className={getStatusBadgeClass(file.status)}>
                                    {formatStatus(file.status)}
                                </span>
                            </Td>
                            <Td>
                                {file.feasible && (
                                    <span className={getStatusBadgeClass(file.feasible)}>
                                        {formatStatus(file.feasible)}
                                    </span>
                                )}
                            </Td>
                            <Td className="text-center">
                                <DownloadModFileButton
                                    fileId={file.id}
                                    fileName={file.mod_file_name}
                                    status={file.status}
                                    price_status={file.price_status}
                                    isTable={true}
                                />
                            </Td>
                            <Td>
                                <div className="flex items-center justify-center gap-1">
                                    <Link
                                        to={`/admin/view-file/${file.id}`}
                                        title="View file"
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-red-500/10 hover:text-red-500 dark:hover:text-orange-400"
                                    >
                                        <EyeIcon className="h-5 w-5"/>
                                    </Link>
                                    <button
                                        title="Delete file"
                                        aria-label="Delete file"
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-rose-500/10 hover:text-rose-500"
                                        onClick={() => handleDeleteClick(file)}
                                    >
                                        <TrashIcon className="h-5 w-5"/>
                                    </button>
                                </div>
                            </Td>
                        </Tr>
                    ))}
                    </tbody>
                </TableShell>
            </Panel>

            <Pagination
                page={paged.page}
                totalPages={paged.totalPages}
                onChange={paged.setPage}
                total={paged.total}
                pageSize={paged.pageSize}
            />

            {showDeleteModal && (
                <ConfirmModal
                    title="Delete file?"
                    message={
                        <>
                            Do you really want to delete{" "}
                            <strong className="text-gray-900 dark:text-white">
                                {truncateText(fileToDelete.file_name, 35)}
                            </strong>
                            ? This cannot be undone.
                        </>
                    }
                    confirmLabel="Delete"
                    busy={loadingToDelete}
                    onConfirm={handleDeleteConfirm}
                    onCancel={handleCloseModal}
                />
            )}

            {showNotification && (
                <Notification
                    setShowNotification={setShowNotification}
                    message="File deleted successfully!"
                    isError={false}
                />
            )}
        </Page>
    );
};

export default AllFiles;
